import React, { useState, useEffect, useRef, forwardRef } from 'react';
import toast from 'react-hot-toast';

const PinInput = forwardRef(
  (
    {
      index,
      value,
      onChange,
      onKeyDown,
      isInvalid,
      onPaste,
    }: {
      index: number;
      value: string;
      onChange: (index: number, value: string) => void;
      onKeyDown: (index: number) => void;
      isInvalid: boolean;
      onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    },
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const isSingleDigit = /^[0-9]$/.test(newValue);
      if (isSingleDigit) {
        onChange(index, newValue);
      }
    };

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (!value) {
          onKeyDown(index - 1);
        } else {
          onChange(index, ''); // Clear the current input value on backspace
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ref.current.focus(); // Keep the focus on the current input after clearing the value
        }
      }
    };

    return (
      <input
        ref={ref}
        type="number"
        inputMode="numeric"
        autoComplete={index === 0 ? 'one-time-code' : 'off'}
        maxLength={1}
        value={value}
        onPaste={onPaste} // Handle the onPaste event
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        className={`w-12 h-12 text-center py-8 text-lg rounded-lg border-2 focus:outline-none ${
          isInvalid ? 'border-red-500' : 'border-gray-300'
        }`}
      />
    );
  },
);
const PinCodeForm = ({
  pinLength = 6,
  onInputChange,
  isInvalid,
  onComplete,
}: {
  pinLength?: number;
  onInputChange?: (value: string) => void;
  isInvalid: boolean;
  onComplete: (value: string) => void;
}) => {
  const [pinValues, setPinValues] = useState(Array(pinLength).fill(''));
  const pinRefs = useRef([]);

  const handleOnChange = (index: number, value: any) => {
    const newPinValues = [...pinValues];
    newPinValues[index] = value;
    setPinValues(newPinValues);

    if (index < pinLength - 1 && value) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      pinRefs.current[index + 1].focus();
    }

    if (onInputChange) {
      onInputChange(newPinValues.join(''));
    }

    if (newPinValues.every((value) => value.trim().length === 1)) {
      onComplete(newPinValues.join(''));
    }
  };

  const handleOnKeyDown = (index: number) => {
    if (index >= 0) {
      const newPinValues = [...pinValues];
      newPinValues[index] = '';
      setPinValues(newPinValues);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      pinRefs.current[index].focus();
    }
  };

  useEffect(() => {
    if (isInvalid && pinValues.every((value) => value.trim().length === 1)) {
      setPinValues(Array(pinLength).fill(''));
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      pinRefs.current[0].focus();
    }
  }, [isInvalid, pinLength, pinValues]);

  useEffect(() => {
    if (pinValues.filter((value) => value).length === 0) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      pinRefs.current[0].focus();
    }
  }, [pinValues, pinRefs]);

  const handleOnPaste = (e: {
    preventDefault: () => void;
    clipboardData: { getData: (arg0: string) => any };
  }) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');

    // Extract all digits from pasted text (handles spaces, newlines, special chars)
    const digitsOnly = pastedData.replace(/\D/g, '');

    if (digitsOnly.length >= pinLength) {
      // Take first N digits
      const code = digitsOnly.slice(0, pinLength);
      const pastedValues = code.split('');

      setPinValues(pastedValues);

      if (onInputChange) {
        onInputChange(code);
      }

      // Always call onComplete when we have a valid code
      onComplete(code);

      // Focus last input
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      pinRefs.current[pinLength - 1]?.focus();
    } else if (digitsOnly.length > 0) {
      // Found some digits but not enough
      toast.error(
        `Found ${digitsOnly.length} digit${
          digitsOnly.length !== 1 ? 's' : ''
        }, need ${pinLength}`,
      );
    } else {
      // No digits found at all
      toast.error('No digits found in pasted text');
    }
  };

  return (
    <div className="flex justify-center space-x-2">
      {pinValues.map((value, index) => (
        <PinInput
          key={index}
          index={index}
          value={value}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          isInvalid={isInvalid}
          onPaste={handleOnPaste}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ref={(el) => (pinRefs.current[index] = el)}
        />
      ))}
    </div>
  );
};

export { PinCodeForm };
